<template lang="pug">
	form
		div.trigger-wrapper: button.trigger(
			type="button",
			@click="toggleControls",
			aria-label="Toggle control panel",
			:class="displayControls ? 'active' : ''"
		)
			span(v-if="displayControls", aria-hidden="true") &times;
			span(v-else, aria-hidden="true") ☰
		div.controls-container(v-show="displayControls")
			span.heading Controls
			fieldset.colors
				legend Initial Shade
				label.use-random-colors.checkbox-container
					input(type="checkbox", v-model="useRandomColors")
					span Random
				label.base-color.color-container(:class="useRandomColors ? 'disabled' : ''")
					input(
						type="color",
						v-model="selectedColor",
						:disabled="useRandomColors",
						@change="setBaseColor",
						aria-label="Seed color"
					)
					span Choose
			fieldset.cell-count
				legend Cell Count
				label.rows
					span Rows
					input(
						type="number",
						v-model.number="rows",
						:max="maxRows",
						:min="minRows"
					)
				label.columns
					span Columns
					input(
						type="number",
						v-model.number="columns",
						:max="maxCols",
						:min="minCols"
					)
			fieldset.timings
				legend Timings
				label.change-frequency
					div(aria-hidden="true")
						span Frequency&nbsp;
						em (ms)
					input(
						type="number",
						v-model.number="mutationInterval",
						min="1",
						:max="Number.MAX_SAFE_INTEGER"
						aria-label="Change frequency in milliseconds"
					)
				label.transition-duration
					div(aria-hidden="true")
						span Duration&nbsp;
						em (ms)
					input(
						type="number",
						v-model.number="transitionDuration",
						min="0",
						:max="Number.MAX_SAFE_INTEGER"
						aria-label="Change duration in milliseconds"
					)
</template>

<script>
	export default {
		name: 'Controls',
		data () {
			return {
				displayControls: false,
				maxCols: 25,
				maxRows: 25,
				minCols: 2,
				minRows: 2,
				selectedColor: '#3399FF',
				simpleUseRandomColors: this.$store.state.baseColor === null
			};
		},
		computed: {
			baseColor: {
				get () { return this.$store.state.baseColor; },
				set (val) { this.$store.commit('setBaseColor', val); }
			},
			columns: {
				get () { return this.$store.state.width; },
				set (val) { if (val >= this.minCols && val <= this.maxCols) this.$store.commit('setWidth', val); }
			},
			mutationInterval: {
				get () { return this.$store.state.mutationInterval; },
				set (val) { this.$store.dispatch('setMutationInterval', val); }
			},
			rows: {
				get () { return this.$store.state.height; },
				set (val) { if (val >= this.minRows && val <= this.maxRows) this.$store.commit('setHeight', val); }
			},
			transitionDuration: {
				get () { return this.$store.state.transitionDuration; },
				set (val) { this.$store.commit('setTransitionDuration', val); }
			},
			useRandomColors: {
				get () { return this.simpleUseRandomColors; },
				set (boolString) {
					const bool = Boolean(boolString);
					this.simpleUseRandomColors = bool;

					if (bool) this.$store.commit('setBaseColor');
					else this.setBaseColor();
				}
			}
		},
		methods: {
			setBaseColor () { this.baseColor = this.selectedColor; },
			toggleControls () { this.displayControls = !this.displayControls; }
		}
	};
</script>

<style lang="scss" scoped>
	form {
		--border-width: 0.25rem;
		--foreground: #000;
		--background: #fff;

		position: absolute;
		top: 1rem;
		right: 1rem;
		font-size: 1.25rem;

		div.trigger-wrapper {
			display: flex;
			justify-content: flex-end;
			align-items: center;
			height: 3rem;

			button.trigger {
				--blur-strength: 0em;

				width: 3rem;
				font-weight: 600;
				display: flex;
				justify-content: center;
				align-items: center;
				cursor: pointer;
				border: none;
				font-size: 3rem;
				line-height: 3rem;
				color: var(--background);
				padding: 0;
				text-shadow:
					0.05em 0.05em var(--blur-strength) var(--foreground),
					0.05em -0.05em var(--blur-strength) var(--foreground),
					-0.05em 0.05em var(--blur-strength) var(--foreground),
					-0.05em -0.05em var(--blur-strength) var(--foreground);
				scale: 1;
				opacity: 0.25;
				transition: scale 50ms ease-in-out, opacity 50ms ease-in-out;
				will-change: scale, opacity;

				&:active { scale: 0.9; }

				&:hover,
				&.active { opacity: 1; }
			}
		}

		div.controls-container {
			background: var(--background);
			/* background: rgba(255, 255, 255, 0.2); */
			backdrop-filter: blur(1rem);
			color: var(--foreground);
			padding: 1rem;
			border-radius: 0.5rem;
			margin-top: 1rem;
			border: var(--border-width) solid var(--foreground);
			width: calc(100vw - 2rem);
			max-width: 30rem;
			font-weight: 400;

			span.heading {
				font-size: 1.75em;
				font-weight: 600;
				display: block;
				border-bottom: var(--border-width) solid currentColor;
			}
		}
	}

	fieldset {
		padding: 0 1rem 1rem 1rem;
		border-width: var(--border-width);
		border-color: var(--foreground);
		margin: 1rem 0;
		display: flex;

		legend {
			font-size: 1.25em;
			font-weight: 600;
		}

		&.colors {
			justify-content: space-between;

			label.base-color {
				flex-basis: 30%;
				display: flex;
				flex-direction: column;
				justify-content: center;
				align-items: center;
			}
		}

		&:not(.colors) label + label { margin-left: 1rem; }
	}

	label {
		display: flex;
		margin: 0.5rem 0;

		span { font-size: 0.9em; }

		em { font-size: 0.75em; }

		&:not(.checkbox-container,.color-container) {
			flex-direction: column;
		}
	}

	label.use-random-colors,
	label.base-color {
		justify-content: flex-start;
		align-items: center;

		input { margin-right: 0.5rem; }
	}

	label.base-color.disabled {
		filter: grayscale(1);
		/* text-decoration: line-through; */
		/* font-style: italic; */
	}

	input[type="color"] { border-width: var(--border-width); }
</style>
